import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  User,
  Calendar,
  Home,
  CreditCard,
  FileText,
  Phone,
} from 'lucide-react';

function ContextPanel({ context, snapshot, mode = 'live' }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const hasChanged = (snapshotValue, liveValue) => {
    if (!snapshot || !context) return false;
    return JSON.stringify(snapshotValue) !== JSON.stringify(liveValue);
  };

  const FieldRow = ({ label, value, snapshotValue, liveValue, icon: Icon }) => {
    const changed = hasChanged(snapshotValue, liveValue);

    return (
      <div className={`context-field-row ${changed ? 'context-field-changed' : ''}`}>
        {Icon && <Icon size={14} className="context-field-icon" />}
        <span className="context-field-label">{label}:</span>
        <span className="context-field-value">{value || '—'}</span>
        {changed && (
          <span className="context-field-diff" title={`Current: ${liveValue}`}>
            Changed
          </span>
        )}
      </div>
    );
  };

  const displayContext = mode === 'snapshot' && snapshot ? snapshot : context;

  if (!displayContext) {
    return (
      <div className="context-panel">
        <div className="context-panel-empty">No context available</div>
      </div>
    );
  }

  const snapshotDate = snapshot?.reservation?.createdAt || snapshot?.timestamp;

  return (
    <div className="context-panel">
      <button
        className="context-panel-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span>Email Context</span>
        {mode === 'live' ? (
          <span className="context-mode-badge context-mode-live">Live Data</span>
        ) : (
          <span className="context-mode-badge context-mode-snapshot">
            Snapshot {snapshotDate && `from ${new Date(snapshotDate).toLocaleDateString()}`}
          </span>
        )}
      </button>

      {mode === 'snapshot' && (
        <div className="context-snapshot-note">
          This is the data that was used when the email was sent. It may differ from current
          reservation data.
        </div>
      )}

      {isExpanded && (
        <div className="context-panel-content">
          {displayContext.customer && (
            <div className="context-section">
              <div className="context-section-header">
                <User size={16} />
                <span>Guest Information</span>
              </div>
              <div className="context-section-body">
                <FieldRow
                  label="Name"
                  value={`${displayContext.customer.firstName || ''} ${displayContext.customer.lastName || ''}`.trim()}
                  snapshotValue={snapshot?.customer?.firstName + ' ' + snapshot?.customer?.lastName}
                  liveValue={context?.customer?.firstName + ' ' + context?.customer?.lastName}
                />
                <FieldRow
                  label="Email"
                  value={displayContext.customer.email}
                  snapshotValue={snapshot?.customer?.email}
                  liveValue={context?.customer?.email}
                />
                <FieldRow
                  label="Phone"
                  value={displayContext.customer.phone}
                  snapshotValue={snapshot?.customer?.phone}
                  liveValue={context?.customer?.phone}
                />
              </div>
            </div>
          )}

          {displayContext.reservation && (
            <div className="context-section">
              <div className="context-section-header">
                <Calendar size={16} />
                <span>Reservation Details</span>
              </div>
              <div className="context-section-body">
                <FieldRow
                  label="Reservation ID"
                  value={displayContext.reservation.id}
                  snapshotValue={snapshot?.reservation?.id}
                  liveValue={context?.reservation?.id}
                />
                <FieldRow
                  label="Check-in"
                  value={displayContext.reservation.checkIn}
                  snapshotValue={snapshot?.reservation?.checkIn}
                  liveValue={context?.reservation?.checkIn}
                />
                <FieldRow
                  label="Check-out"
                  value={displayContext.reservation.checkOut}
                  snapshotValue={snapshot?.reservation?.checkOut}
                  liveValue={context?.reservation?.checkOut}
                />
                <FieldRow
                  label="Nights"
                  value={displayContext.reservation.nights}
                  snapshotValue={snapshot?.reservation?.nights}
                  liveValue={context?.reservation?.nights}
                />
                <FieldRow
                  label="Status"
                  value={
                    <span className={`context-status-badge context-status-${displayContext.reservation.status}`}>
                      {displayContext.reservation.status}
                    </span>
                  }
                  snapshotValue={snapshot?.reservation?.status}
                  liveValue={context?.reservation?.status}
                />
                {displayContext.reservation.refundable !== undefined && (
                  <FieldRow
                    label="Refundable"
                    value={displayContext.reservation.refundable ? 'Yes' : 'No'}
                    snapshotValue={snapshot?.reservation?.refundable}
                    liveValue={context?.reservation?.refundable}
                  />
                )}
              </div>
            </div>
          )}

          {displayContext.property && (
            <div className="context-section">
              <div className="context-section-header">
                <Home size={16} />
                <span>Property</span>
              </div>
              <div className="context-section-body">
                <FieldRow
                  label="Name"
                  value={displayContext.property.name}
                  snapshotValue={snapshot?.property?.name}
                  liveValue={context?.property?.name}
                />
                <FieldRow
                  label="Address"
                  value={`${displayContext.property.address || ''}, ${displayContext.property.city || ''}`.trim()}
                  snapshotValue={snapshot?.property?.address + ', ' + snapshot?.property?.city}
                  liveValue={context?.property?.address + ', ' + context?.property?.city}
                />
                {displayContext.property.checkInTime && (
                  <FieldRow
                    label="Check-in Time"
                    value={displayContext.property.checkInTime}
                    snapshotValue={snapshot?.property?.checkInTime}
                    liveValue={context?.property?.checkInTime}
                  />
                )}
                {displayContext.property.checkOutTime && (
                  <FieldRow
                    label="Check-out Time"
                    value={displayContext.property.checkOutTime}
                    snapshotValue={snapshot?.property?.checkOutTime}
                    liveValue={context?.property?.checkOutTime}
                  />
                )}
              </div>
            </div>
          )}

          {displayContext.payment && (
            <div className="context-section">
              <div className="context-section-header">
                <CreditCard size={16} />
                <span>Payment</span>
              </div>
              <div className="context-section-body">
                <FieldRow
                  label="Status"
                  value={
                    <span className={`context-status-badge context-payment-${displayContext.payment.status}`}>
                      {displayContext.payment.status}
                    </span>
                  }
                  snapshotValue={snapshot?.payment?.status}
                  liveValue={context?.payment?.status}
                />
                <FieldRow
                  label="Amount"
                  value={`${displayContext.payment.amount} ${displayContext.payment.currency || 'USD'}`}
                  snapshotValue={snapshot?.payment?.amount}
                  liveValue={context?.payment?.amount}
                />
                {displayContext.payment.dueDate && (
                  <FieldRow
                    label="Due Date"
                    value={displayContext.payment.dueDate}
                    snapshotValue={snapshot?.payment?.dueDate}
                    liveValue={context?.payment?.dueDate}
                  />
                )}
              </div>
            </div>
          )}

          {displayContext.policy && (
            <div className="context-section">
              <div className="context-section-header">
                <FileText size={16} />
                <span>Policy</span>
              </div>
              <div className="context-section-body">
                <FieldRow
                  label="Cancellation"
                  value={displayContext.policy.cancellationPolicy}
                  snapshotValue={snapshot?.policy?.cancellationPolicy}
                  liveValue={context?.policy?.cancellationPolicy}
                />
                {displayContext.policy.cancellationDeadline && (
                  <FieldRow
                    label="Deadline"
                    value={displayContext.policy.cancellationDeadline}
                    snapshotValue={snapshot?.policy?.cancellationDeadline}
                    liveValue={context?.policy?.cancellationDeadline}
                  />
                )}
              </div>
            </div>
          )}

          {displayContext.support && (
            <div className="context-section">
              <div className="context-section-header">
                <Phone size={16} />
                <span>Support</span>
              </div>
              <div className="context-section-body">
                <FieldRow
                  label="Email"
                  value={displayContext.support.email}
                  snapshotValue={snapshot?.support?.email}
                  liveValue={context?.support?.email}
                />
                <FieldRow
                  label="Phone"
                  value={displayContext.support.phone}
                  snapshotValue={snapshot?.support?.phone}
                  liveValue={context?.support?.phone}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ContextPanel;
